import { Plane } from "lucide-react";
import { useTranslation } from "react-i18next";
import MiniTourCard from "@/components/cards/MiniTourCard";
import LayoverMiniTourTeaser from "@/components/cards/LayoverMiniTourTeaser";
import { useGeo } from "@/context/GeoContext";
import { buildAffiliateHref } from "@/lib/affiliate";
import { resolveDisplay, formatPrice, formatDuration, formatStops } from "@/lib/currency";

function formatTravelDate(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso + "T00:00:00");
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleDateString(undefined, {
            weekday: "short",
            day: "numeric",
            month: "short",
            year: d.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
        });
    } catch {
        return iso;
    }
}

export default function FlightOptionCard({ option }) {
    const { geo, currencyPref } = useGeo();
    const { t } = useTranslation();
    const region = geo?.affiliate_region || "WORLD";
    const {
        id,
        airline,
        flight_number,
        origin,
        destination,
        duration_minutes,
        stops,
        layovers = [],
        price,
        display_price,
        is_cheapest,
        is_preferred_timing,
        is_recommended,
        book_url,
        mini_tour,
        is_grounded,
        source_label,
        confidence,
        sample_date_indicative,
        sample_date,
    } = option;

    const stopsLabel = formatStops(stops, layovers[0]?.code);
    const durationLabel = formatDuration(duration_minutes);
    // Phase 14.9.6 — fallback "search shortcut" cards (Google Flights /
    // Skyscanner) come from `_universal_fallbacks.build_flight_fallback_cards`
    // and intentionally carry NO `origin.time`, `destination.time`,
    // `duration_minutes`, or `stops`. Detect that shape and skip the
    // timeline section entirely instead of rendering "NaNm" / "undefined
    // stops" (production regression 2026-06-19).
    const hasTimeline = Boolean(origin?.time && destination?.time);
    // Phase 14.9.7 — when TP couldn't price the user's literal date and
    // we surfaced nearby-crawled-date rows instead, surface an honest
    // "Indicative pricing" pill so the user is never misled into
    // thinking we quoted their specific travel date.
    const isIndicative = Boolean(sample_date_indicative);

    const { display, original, sameCurrency } = resolveDisplay(
        price,
        display_price,
        currencyPref,
    );

    return (
        <article
            className="relative overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white p-5 transition hover:border-[#0A0A0A]"
            data-testid={`flight-option-${id}`}
        >
            {/* Top-right colored dot — Flights = Blue */}
            <span
                aria-hidden
                className="absolute right-4 top-4 inline-block h-2.5 w-2.5 rounded-full bg-[#2563EB] ring-4 ring-[#2563EB]/10"
                data-testid="card-color-dot"
            />

            <div className="flex flex-col gap-5 md:flex-row md:items-stretch md:justify-between">
                {/* Left: airline + timeline */}
                <div className="flex-1 min-w-0">
                    {/* Airline header */}
                    <div className="flex items-center gap-2">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#F5F5F5]">
                            <Plane size={13} strokeWidth={2} className="text-[#0A0A0A]" />
                        </span>
                        <span className="text-[13px] font-medium text-[#0A0A0A]">
                            {airline}
                        </span>
                        <span className="text-[12px] text-[#A3A3A3]">·</span>
                        <span className="text-[12px] text-[#737373]">
                            {flight_number}
                        </span>
                    </div>

                    {/* Phase 8M.3 Item 5 — prominent travel date row */}
                    {(origin.date || destination.date) && (
                        <div
                            className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#F5F5F5] px-2.5 py-1 text-[11.5px] font-medium text-[#0A0A0A]"
                            data-testid="flight-travel-date"
                        >
                            <span className="text-[#737373]">{t("cards.travel_date_label")}:</span>
                            <span>{formatTravelDate(origin.date) || formatTravelDate(destination.date)}</span>
                        </div>
                    )}

                    {/* Phase 14.9.7 — Indicative pricing pill. Shown when
                        TP had no inventory for the user's literal date
                        and we surfaced nearby-crawled-date rows. */}
                    {isIndicative && (
                        <div
                            className="mt-2 ml-2 inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-2.5 py-1 text-[11px] font-medium text-[#737373]"
                            data-testid="flight-indicative-pill"
                            title={sample_date ? `Sampled from ${sample_date}` : ""}
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-[#A3A3A3]" />
                            Indicative pricing for upcoming dates
                        </div>
                    )}

                    {/* Timeline — rendered only when we have flight times.
                        Fallback "search shortcut" cards skip this and just
                        show the airline label + Book Now CTA on the right. */}
                    {hasTimeline && (
                    <div className="mt-4 flex items-center gap-3">
                        <div className="text-left">
                            <div className="text-[20px] font-semibold leading-tight tracking-tight">
                                {origin.time}
                            </div>
                            <div className="mt-0.5 text-[12px] font-medium uppercase tracking-wider text-[#737373]">
                                {origin.code}
                            </div>
                            <div className="text-[11px] text-[#A3A3A3]">
                                {origin.city}
                            </div>
                        </div>

                        <div className="flex-1 px-1">
                            <div className="flex flex-col items-center">
                                {durationLabel && (
                                    <div className="text-[11px] font-medium text-[#737373]">
                                        {durationLabel}
                                    </div>
                                )}
                                <div className="my-1 flex w-full items-center">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#0A0A0A]" />
                                    <span className="h-px flex-1 bg-[#E5E5E5]" />
                                    {stops > 0 && (
                                        <>
                                            <span className="h-1.5 w-1.5 rounded-full border border-[#0A0A0A] bg-white" />
                                            <span className="h-px flex-1 bg-[#E5E5E5]" />
                                        </>
                                    )}
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#0A0A0A]" />
                                </div>
                                {stopsLabel && (
                                    <div className="text-[11px] text-[#A3A3A3]">
                                        {stopsLabel}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="text-right">
                            <div className="text-[20px] font-semibold leading-tight tracking-tight">
                                {destination.time}
                            </div>
                            <div className="mt-0.5 text-[12px] font-medium uppercase tracking-wider text-[#737373]">
                                {destination.code}
                            </div>
                            <div className="text-[11px] text-[#A3A3A3]">
                                {destination.city}
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Route summary for fallback search-shortcut cards
                        (when timeline is suppressed). Keeps the user
                        oriented without rendering blank time slots. */}
                    {!hasTimeline && (origin?.city || destination?.city) && (
                        <div className="mt-3 text-[13px] font-medium text-[#0A0A0A]">
                            {origin?.city || origin?.code || "—"}
                            <span className="px-2 text-[#A3A3A3]">→</span>
                            {destination?.city || destination?.code || "—"}
                        </div>
                    )}

                    {/* Chips */}
                    <div className="mt-4 flex flex-wrap gap-1.5">
                        {is_cheapest && (
                            <span
                                className="rounded-full bg-[#0A0A0A] px-2.5 py-0.5 text-[11px] font-medium tracking-wide text-white"
                                data-testid="chip-cheapest"
                            >
                                Cheapest
                            </span>
                        )}
                        {is_recommended && (
                            <span
                                className="rounded-full border border-[#F59E0B] bg-white px-2.5 py-0.5 text-[11px] font-medium tracking-wide text-[#F59E0B]"
                                data-testid="chip-recommended"
                                title={t("cards.recommended_tooltip")}
                            >
                                {t("cards.recommended")}
                            </span>
                        )}
                        {is_preferred_timing && (
                            <span
                                className="rounded-full border border-[#E5E5E5] bg-white px-2.5 py-0.5 text-[11px] font-medium tracking-wide text-[#0A0A0A]"
                                data-testid="chip-preferred"
                            >
                                Preferred timing
                            </span>
                        )}
                        {stops === 0 && (
                            <span className="rounded-full border border-[#E5E5E5] bg-white px-2.5 py-0.5 text-[11px] font-medium tracking-wide text-[#0A0A0A]">
                                Non-stop
                            </span>
                        )}
                    </div>
                </div>

                {/* Right: price + CTA */}
                <div className="flex items-end justify-between gap-3 border-t border-[#E5E5E5] pt-4 md:flex-col md:items-end md:justify-center md:border-l md:border-t-0 md:pl-6 md:pt-0">
                    <div className="text-right">
                        <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">
                            Total
                        </div>
                        <div
                            className="text-[24px] font-semibold leading-tight tracking-tight text-[#0A0A0A]"
                            data-testid="flight-price"
                        >
                            {formatPrice(display)}
                        </div>
                        {!sameCurrency && (currencyPref || "USD").toUpperCase() === "USD" && (
                            <div
                                className="mt-0.5 text-[11px] text-[#A3A3A3]"
                                data-testid="flight-price-original"
                            >
                                {formatPrice(original)} {original.currency}
                            </div>
                        )}
                        {is_grounded && confidence !== "high" && (
                            <div
                                className="mt-0.5 text-[10px] italic text-[#A3A3A3]"
                                data-testid="flight-price-approx"
                            >
                                approx
                            </div>
                        )}
                    </div>
                    {!!book_url && (
                        <a
                            href={is_grounded ? book_url : buildAffiliateHref(book_url, region)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`book-now-${id}`}
                            className="inline-flex items-center justify-center rounded-lg bg-[#0A0A0A] px-5 py-2.5 text-[13px] font-medium text-white transition hover:bg-[#262626]"
                        >
                            {is_grounded ? t("cards.view_on", { source: source_label || "Web" }) : t("cards.book_now")}
                        </a>
                    )}
                </div>
            </div>

            {/* Phase 8 — collapsed-by-default teaser pill for the
                richest annotated layover. Replaces the auto-expanded
                MiniTourCard when the new `layovers[i].mini_tour_eligible`
                flag is present (Phase 8 backend ≥7h). For legacy flights
                that still carry the compact `mini_tour` shape, fall
                through to the original MiniTourCard render. */}
            {layovers?.some(lv => lv?.mini_tour_eligible) ? (
                <LayoverMiniTourTeaser
                    layover={
                        // Show teaser for the longest eligible layover.
                        layovers
                            .filter(lv => lv.mini_tour_eligible)
                            .sort((a, b) => (b.duration_min || 0) - (a.duration_min || 0))[0]
                    }
                />
            ) : mini_tour ? (
                <div className="mt-5">
                    <MiniTourCard tour={mini_tour} />
                </div>
            ) : null}
        </article>
    );
}
